import { locateNode, canEditNode } from './node-operations'
import { numericProperty } from '@/runtime/device-chart-data'
import { createListComponent } from '@/components/FleetList/model'

const kinds = ['text', 'gauge', 'line', 'bar', 'productList', 'deviceList']
const valueKinds = ['text', 'gauge', 'line', 'bar']
const styleKinds = ['text', 'productList', 'deviceList']
const copy = value => JSON.parse(JSON.stringify(value))
const object = value => value && typeof value === 'object' && !Array.isArray(value)
const own = (value, key) => Object.prototype.hasOwnProperty.call(value, key)
const fail = message => { throw new Error(message) }

function walk (nodes, visit) {
  nodes.forEach(node => { visit(node); if (Array.isArray(node.children)) walk(node.children, visit) })
}

function nativeKind (node, kind) {
  return !!node.component && node.component.name === kind && node.component.prop === kind
}

// 分组按叶节点处理；某个兄弟被锁定不会阻止其他可编辑叶节点。
export function quickOperationTargets (nodes, active, kind) {
  const targets = [], visited = new Set()
  let skipped = 0
  const visit = node => {
    if (visited.has(node.index)) return
    visited.add(node.index)
    if (Array.isArray(node.children)) { node.children.forEach(visit); return }
    if (!nativeKind(node, kind) || !canEditNode(locateNode(nodes, node.index))) skipped++
    else targets.push(node)
  }
  ;(Array.isArray(active) ? active : []).forEach(id => {
    const item = locateNode(nodes, id)
    if (item) visit(item.node)
    else if (!visited.has(id)) { visited.add(id); skipped++ }
  })
  return { targets, skipped }
}

function ids (values, label) {
  if (!Array.isArray(values) || !values.length || values.length > 50) fail(label + '请选择 1～50 项')
  const used = new Set()
  return values.map(value => {
    if (typeof value !== 'string' || !value.trim() || value.length > 256 || used.has(value)) fail(label + '编号为空、重复或格式不正确')
    used.add(value)
    return value
  })
}

function propertiesFor (form, properties) {
  const selected = ids(form.propertyIds, '属性')
  return selected.map(id => propertyFor(id, form.kind, properties))
}

function propertyFor (id, kind, properties) {
  const matches = properties.filter(property => property && property.id === id)
  if (matches.length !== 1) fail('属性不存在或已失效，请重新选择物模型属性')
  const property = matches[0]
  if (kind !== 'text' && !numericProperty(property)) fail('仪表盘、折线图和柱状图仅支持数值属性')
  return property
}

function bindingFor (binding = {}) {
  if (!['fixed', 'page'].includes(binding.deviceSource)) fail('请选择有效的设备来源')
  const checked = (value, label) => {
    if (typeof value !== 'string' || !value.trim() || value.length > 256) fail(label + '无效，请重新选择')
    return value
  }
  const productId = checked(binding.productId, '产品编号')
  return { deviceSource: binding.deviceSource,
    deviceId: binding.deviceSource === 'fixed' ? checked(binding.deviceId, '设备编号') : '',
    deviceName: binding.deviceSource === 'fixed' ? String(binding.deviceName || '').slice(0, 256) : '',
    productId, productName: String(binding.productName || '').slice(0, 256) }
}

function bindProperty (node, property, binding, creating) {
  const previous = node.parameter || {}
  const parameter = {}
  // 重绑只保留查询区间/聚合设置，不把旧接口参数带到新数据源。
  if (!creating) ['configDataType', 'limit', 'from', 'to', 'rangeMode', 'aggregationType', 'time', 'interval', 'format', 'liveFollow', 'refreshInterval']
    .forEach(key => { if (previous[key] !== undefined) parameter[key] = previous[key] })
  if (creating && ['line', 'bar'].includes(node.component.prop)) Object.assign(parameter, {
    configDataType: 2, limit: 200, from: 'now-1h', to: 'now', rangeMode: 'relative',
    aggregationType: 'AVG', interval: '1h', format: 'yyyy-MM-dd HH:mm:ss', liveFollow: true, refreshInterval: 0
  })
  Object.assign(parameter, binding, { property: property.id, propertyName: property.name || property.id,
    propertyType: property.type || '', propertyUnit: property.unit || '', formatterInput: 'raw' })
  node.parameter = parameter
  node.dataType = 2
  node.dataMethod = 'get'
  node.dataFormatter = ''; node.dataScript = ''; node.url = ''; node.dataQuery = ''; node.time = 0
  if (own(node, 'echartFormatter')) node.echartFormatter = ''
  const kind = node.component.prop
  if (kind === 'text') node.data = { value: '' }
  else if (kind === 'gauge') {
    const old = object(node.data) ? node.data : {}
    const min = Number(creating ? property.min : old.min), max = Number(creating ? property.max : old.max)
    const validRange = Number.isFinite(min) && Number.isFinite(max) && min < max
    node.data = { min: validRange ? min : 0, max: validRange ? max : 100,
      label: property.name || property.id, value: null, unit: property.unit || '' }
  } else node.data = { categories: [], series: [{ name: property.name || property.id, data: [] }] }
}

function styleFor (style) {
  if (!object(style) || Object.keys(style).some(key => !['fontSize', 'color'].includes(key)) || !Object.keys(style).length) fail('请选择要修改的字体大小或文字颜色')
  const result = {}
  if (own(style, 'fontSize')) {
    const value = Number(style.fontSize)
    if (!Number.isInteger(value) || value < 10 || value > 48) fail('批量字体大小应为 10～48 的整数')
    result.fontSize = value
  }
  if (own(style, 'color')) {
    if (typeof style.color !== 'string' || !/^#[\da-f]{6}$/i.test(style.color)) fail('文字颜色应为有效的六位十六进制颜色')
    result.color = style.color
  }
  return result
}

function layoutFor (layout = {}, config = {}, count) {
  const number = (value, min, max, label) => {
    const result = Number(value)
    if (!Number.isInteger(result) || result < min || result > max) fail(label + '超出允许范围')
    return result
  }
  const canvasWidth = number(config.width, 1, 100000, '画布宽度'), canvasHeight = number(config.height, 1, 100000, '画布高度')
  const x = number(layout.x, 0, canvasWidth, '起始横坐标'), y = number(layout.y, 0, canvasHeight, '起始纵坐标')
  const width = number(layout.width, 20, canvasWidth, '组件宽度'), height = number(layout.height, 20, canvasHeight, '组件高度')
  const columns = number(layout.columns, 1, 50, '每行列数'), gap = number(layout.gap, 0, 2000, '组件间距')
  return Array.from({ length: count }, (_, index) => {
    const left = x + (index % columns) * (width + gap), top = y + Math.floor(index / columns) * (height + gap)
    if (left + width > canvasWidth || top + height > canvasHeight) fail('生成组件将超出画布，请调整数量、尺寸或布局')
    return { left, top, width, height }
  })
}

function projectState (context) {
  const used = new Set()
  let total = 0, matched = false
  const count = nodes => walk(nodes, node => { used.add(node.index); total++ })
  const pages = context.pageProject && context.pageProject.pages
  if (Array.isArray(pages)) pages.forEach(page => {
    if (page.id === context.activePageId || page.nodes === context.nodes || page.config === context.config) {
      if (!matched) count(context.nodes)
      matched = true
    } else count(page.nodes || [])
  })
  if (!matched) count(context.nodes)
  return { used, total }
}

function allocateId (createId, used) {
  if (typeof createId !== 'function') fail('组件编号生成器不可用')
  for (let attempt = 0; attempt < 20; attempt++) {
    const id = createId()
    if (typeof id !== 'string' || !/^[A-Za-z0-9_-]{1,128}$/.test(id)) fail('组件编号生成失败，请重试')
    if (!used.has(id)) { used.add(id); return id }
  }
  fail('组件编号重复，请重试')
}

function listBinding (node, kind, products) {
  node.data = []; node.dataType = 0; node.time = 0
  node.dataFormatter = ''; node.dataScript = ''; node.url = ''; node.dataQuery = ''
  node.option = { ...(node.option || {}), dataSource: 'platform', productScope: 'specified',
    productIds: kind === 'productList' ? products.slice() : [], productId: kind === 'deviceList' ? products[0] : '', previewProductId: '' }
}

// 预览和应用共用纯计算；全部通过后才返回新节点，失败不会部分修改草稿。
export function buildQuickOperation (form, context, properties = [], presets = [], createId) {
  if (!object(form) || !object(context) || !Array.isArray(context.nodes)) fail('快捷操作上下文不可用')
  if (!['batch-create', 'batch-bind', 'batch-style'].includes(form.action) || !kinds.includes(form.kind)) fail('暂不支持该快捷操作或组件类型')
  if (!['device-property', 'device-product'].includes(form.source)) fail('当前快捷操作暂不支持该数据源')
  if (form.action === 'batch-style' && !styleKinds.includes(form.kind)) fail('批量样式仅支持文本、产品列表和设备列表')
  if (form.action !== 'batch-style' && ((form.source === 'device-property') !== valueKinds.includes(form.kind))) fail('数据源与组件类型不匹配')
  const state = projectState(context), nodes = copy(context.nodes), rows = []
  let selected, products, binding, targets = [], skipped = 0
  if (form.action !== 'batch-style') {
    if (form.source === 'device-property') {
      binding = bindingFor(form.binding)
      if (form.action === 'batch-create') selected = propertiesFor(form, properties)
    } else products = ids(form.productIds, '产品')
  }
  const productLabel = id => String((form.productNames && own(form.productNames, id) && form.productNames[id]) || id)
  if (form.action !== 'batch-create') {
    const result = quickOperationTargets(nodes, context.active, form.kind)
    targets = result.targets; skipped = result.skipped
    if (!targets.length) fail('没有可操作的组件，请先选中对应类型的未锁定组件')
    if (targets.length > 50) fail('单次最多操作 50 个组件')
    if (form.action === 'batch-style') {
      const style = styleFor(form.style)
      targets.forEach(node => { node.option = { ...(node.option || {}), ...style }; rows.push({ name: node.title || node.name || node.index, field: Object.keys(style).map(key => key === 'fontSize' ? '字体大小' : '文字颜色').join('、') }) })
    } else if (form.source === 'device-property') {
      targets.forEach(node => {
        const id = form.mappings && own(form.mappings, node.index) ? form.mappings[node.index] : ''
        const property = propertyFor(id, form.kind, properties)
        bindProperty(node, property, binding, false)
        rows.push({ name: node.title || node.name || node.index, field: (property.name || property.id) + ' · ' + property.id })
      })
    } else {
      if (form.kind === 'deviceList' && products.length !== 1) fail('批量绑定设备列表时请选择一个产品')
      targets.forEach(node => { listBinding(node, form.kind, products); rows.push({ name: node.title || node.name || node.index, field: products.map(productLabel).join('、') }) })
    }
    return { nodes, rows, count: targets.length, skipped }
  }
  const entries = form.source === 'device-property' ? selected : form.kind === 'productList' ? [products] : products.map(id => [id])
  if (entries.length > 50) fail('单次最多生成 50 个组件')
  const max = context.isTemplateEditing ? 1000 : 3000
  if (state.total + entries.length > max) fail('生成后项目组件总数超过 ' + max + '，请减少组件后重试')
  const positions = layoutFor(form.layout, context.config, entries.length)
  const added = entries.map((entry, index) => {
    const preset = presets.find(node => nativeKind(node, form.kind))
    if (!preset && valueKinds.includes(form.kind)) fail('系统组件定义不可用，请重新加载编辑器')
    const node = preset ? copy(preset) : createListComponent(form.kind === 'productList' ? 'product' : 'device')
    const position = positions[index]
    node.index = allocateId(createId, state.used); node.locked = false; node.left = position.left; node.top = position.top
    node.component.width = position.width; node.component.height = position.height
    if (form.source === 'device-property') {
      node.name = node.title = (entry.name || entry.id).slice(0, 100)
      bindProperty(node, entry, binding, true)
      rows.push({ name: node.title, field: (entry.name || entry.id) + ' · ' + entry.id })
    } else {
      listBinding(node, form.kind, entry)
      node.name = node.title = form.kind === 'productList' ? '产品列表' : productLabel(entry[0]).slice(0, 80) + ' · 设备列表'
      node.option.title = node.title
      rows.push({ name: node.title, field: entry.map(productLabel).join('、') })
    }
    return node
  })
  return { nodes: added.concat(nodes), rows, count: added.length, skipped: 0 }
}
